import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as StripePayoutsIcon } from '../../../assets/icons/sidebar/stripe-payout.svg';
import DefaultApiLimit from '../../../config/defaultLimit';
import {
  RequestStripePayoutsInterface,
  StripePayoutsInterface,
} from '../../../interface/stripe-payouts.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  disableApprovedStripePayoutsApplied,
  fetchApprovedStripePayoutsMethodFilters,
  fetchStripePayouts,
  setSortData,
  updateApprovedStripePayoutsFilters,
  refreshApprovedStripePayouts,
} from '../../../redux/slices/ApprovedStripePayoutsSlice';
import { setPageTitle } from '../../../redux/slices/PageTitleSlice';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import { GetParametersFromCommonFilters } from '../../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../../shared-functions/ConvertUTCtoDate';
import { exportCSV, exportPDF } from '../../../shared-functions/ExportFile';
import { permissionCheck } from '../../../shared-functions/JWTDocodeFunction';
import { capitalizeFirstLetter } from '../../../shared-functions/StringFunctions';
import ToFixedValue from '../../../shared-functions/ToFixedValue';

/**
 * Approved stripe payouts components
 * @returns {any} The rendered component
 */
function ApprovedCPOPayouts() {
  const { t, i18n } = useTranslation('stripePayout');
  const {
    payouts,
    limit,
    page,
    loading,
    totalPages,
    totalResults,
    filters,
    filterListLoading,
    isFilterUpdated,
    sortField,
    sortBy,
  } = useAppSelector((state) => state.stripePayouts);
  const dispatch = useAppDispatch();

  /**
   * Handle the fetch the data in api function
   * @param {any} data - The request data
   */
  const fetchData = (data: RequestStripePayoutsInterface) => {
    if (!loading) {
      dispatch(fetchStripePayouts(data));
    }
  };

  /**
   * Table pagination function
   * @param {any} value - The page props
   */
  const pageNateLimitChange = (value: RequestStripePayoutsInterface) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, ...value, sortField, sortBy });
  };

  const column = [
    {
      label: t('accountID'),
      accessorKey: 'accountCustomId' as keyof StripePayoutsInterface,
      sortAllow: false,
    },
    {
      label: t('accountName'),
      accessorKey: 'accountName' as keyof StripePayoutsInterface,
      sortAllow: true,
    },
    {
      label: t('email'),
      accessorKey: 'email' as keyof StripePayoutsInterface,
      sortAllow: false,
    },
    {
      label: t('revenueFromDate'),
      accessorKey: 'fromDate' as keyof StripePayoutsInterface,
      sortAllow: true,
    },
    {
      label: t('revenueToDate'),
      accessorKey: 'toDate' as keyof StripePayoutsInterface,
      sortAllow: true,
    },
    {
      label: t('payoutID'),
      accessorKey: 'transferId' as keyof StripePayoutsInterface,
      sortAllow: false,
    },
    {
      label: t('payoutAmount'),
      accessorKey: 'amount' as keyof StripePayoutsInterface,
      sortAllow: true,
      /**
       * Payment amount view in table
       * @param {any} payoutsStatus - The payouts status
       * @returns {any} The rendered component
       */
      customData: (payoutsStatus: StripePayoutsInterface) => (
        <div>{ToFixedValue(payoutsStatus.amount)}</div>
      ),
    },
    {
      label: t('status'),
      accessorKey: 'status' as keyof StripePayoutsInterface,
      sortAllow: false,
      /**
       * Payment status view in table
       * @param {any} payoutsStatus - The payouts status
       * @returns {any} The rendered component
       */
      customData: (payoutsStatus: StripePayoutsInterface) => (
        <div className="status-in-stripe-payout-table">
          <p className={`${payoutsStatus.status === 'transferred' ? 'success-text' : null}`}>
            {capitalizeFirstLetter(payoutsStatus.status)}
          </p>
        </div>
      ),
    },
    {
      label: t('payoutDate'),
      accessorKey: 'transferredAt' as keyof StripePayoutsInterface,
      sortAllow: true,
    },
  ];
  /**
   * Handle the filter data clear function
   * @param {any} sortData - The sortData
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Handle the auto refresh data function
   */
  const handleAutoRefresh = () => {
      const queryParameters = GetParametersFromCommonFilters(filters);
  
      dispatch(
        refreshApprovedStripePayouts({
          ...queryParameters,
          page,
          limit,
          sortBy,
          sortField,
        }),
      );
    };
  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, page, limit, sortField, sortBy });
  };
  /**
   * Export data pdf or csv function
   * @param {string} type - The type of export
   */
  const exportDataPdfOrCSV = (type: string) => {
    const headers = [
      t('accountID'),
      t('accountName'),
      t('email'),
      t('revenueFromDate'),
      t('revenueToDate'),
      t('payoutID'),
      t('payoutAmount'),
      t('status'),
      t('payoutDate'),
    ];
    const rows = payouts.map((row: StripePayoutsInterface) => [
      row.accountCustomId || 'N/A',
      row.fromDate ? ConvertUTCtoDate(row.fromDate) : t('na'),
      row.toDate ? ConvertUTCtoDate(row.toDate) : t('na'),
      row.amount ? ToFixedValue(row.amount) : '0.00',
      row.status ? capitalizeFirstLetter(row.status) : t('na'),
      row.transferredAt ? ConvertUTCtoDate(row.transferredAt) : t('na'),
    ]);
    if (rows.length > 0) {
      if (type === 'csv') {
        exportCSV(headers, rows, 'stripe-payouts.csv');
      } else {
        exportPDF(headers, rows, 'stripe-payouts.pdf');
      }
    }
  };

  useEffect(() => {
    if (isFilterUpdated || payouts.length === 0) {
      const queryParameters = GetParametersFromCommonFilters(filters);
      fetchData({
        page: DefaultApiLimit.page,
        limit,
        ...queryParameters,
      });
      dispatch(disableApprovedStripePayoutsApplied());
    }
  }, [filters]);
  useEffect(() => {
    dispatch(
      setPageTitle({
        title: t('cpoPayouts'),
        icon: <StripePayoutsIcon />,
        count: totalResults,
      }),
    );
  }, [totalResults, t]);

  return (
    <div className="table-container" dir={i18n.dir()}>
      <CustomTable
        header={column}
        data={payouts}
        version="v2"
        serialNo
        created
        lastUpdated
        limit={limit}
        page={page}
        loading={loading}
        totalPages={totalPages}
        totalResults={totalResults}
        sortData={{ sortBy, sortField }}
        handleSortChange={handleSortFunction}
        pageLimitChange={pageNateLimitChange}
        sizeChangeButtonRequired
        filters={filters}
        filtersToDispatch={updateApprovedStripePayoutsFilters}
        filterListLoading={filterListLoading}
        filterListToDispatch={fetchApprovedStripePayoutsMethodFilters}
        requiredRefreshToggle
        refreshRequired
        refreshFunction={handleAutoRefresh}
        height={320}
        refershCurrentPages={handleRefresh}
        handleRefresh={handleRefresh}
        exportDataPdfOrCSV={permissionCheck('cpoCpoPayoutsExport') ? exportDataPdfOrCSV : undefined}
        hoverLabel={t('refresh')}
      />
    </div>
  );
}

export default ApprovedCPOPayouts;
